import React, { useState } from 'react';
import { Header, Navbar, Nav, IconButton } from 'rsuite';
import { useNavigate, useLocation } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import MenuIcon from '@rsuite/icons/Menu';
import CloseIcon from '@rsuite/icons/Close';
import { useAuth } from '../Auth/AuthContext';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';

const MOBILE_MENU = {
  zIndex: 999,
  itemPaddingV: 14,
  itemPaddingH: 20,
  sectionPaddingTop: 16,
  sectionPaddingBottom: 8,
  sectionFontSize: 11,
  sectionLetterSpacing: '0.08em',
  dividerMarginV: 8,
  indentPaddingH: 36,
  activeAccentWidth: 3,
  itemFontSize: 14,
};

const getActiveKey = (pathname, search) => {
  if (pathname === '/cardsearch' || pathname.startsWith('/cardsearch/')) return 'cardSearch';
  if (pathname === '/setsearch' || pathname.startsWith('/set/')) return 'setSearch';
  if (pathname === '/rules' || pathname.startsWith('/rules/')) return 'rules';
  if (pathname === '/collection') return 'collection';
  if (pathname === '/library' || pathname.startsWith('/library/')) {
    const type = new URLSearchParams(search).get('type');
    if (type === 'DECK') return 'library-decks';
    if (type === 'TRADE_BINDER') return 'library-binders';
    if (type === 'WISHLIST') return 'library-wishlists';
    return 'library';
  }
  if (pathname === '/settings') return 'settings';
  return null;
};

const MobileNavItem = ({ label, active, onClick, colors, indent }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'block',
      width: '100%',
      textAlign: 'left',
      border: 'none',
      background: 'none',
      padding: `${MOBILE_MENU.itemPaddingV}px ${MOBILE_MENU.itemPaddingH}px`,
      paddingLeft: indent ? MOBILE_MENU.indentPaddingH : MOBILE_MENU.itemPaddingH,
      color: active ? colors.accentPrimary : colors.textPrimary,
      borderLeft: active
        ? `${MOBILE_MENU.activeAccentWidth}px solid ${colors.accentPrimary}`
        : `${MOBILE_MENU.activeAccentWidth}px solid transparent`,
      cursor: 'pointer',
      fontWeight: active ? 600 : 400,
      fontSize: MOBILE_MENU.itemFontSize,
    }}
  >
    {label}
  </button>
);

const NavigationBar = () => {
  const { isAuthenticated, logout } = useAuth();
  const { preferences } = usePreferences();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const active = getActiveKey(location.pathname, location.search);
  const navAppearance = preferences.theme === 'light' ? 'default' : 'inverse';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNavigateMobile = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleLogoutMobile = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  if (isMobile) {
    const t = getThemeColors(preferences.theme);
    return (
      <Header>
        <Navbar appearance={navAppearance}>
          <Navbar.Brand onClick={() => handleNavigateMobile('/')} style={{ cursor: 'pointer' }}>
            CollectNDeck
          </Navbar.Brand>
          <Nav pullRight>
            <Nav.Item style={{ display: 'flex', alignItems: 'center', paddingRight: 8 }}>
              <IconButton
                appearance="subtle"
                size="sm"
                icon={mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                onClick={() => setMobileMenuOpen((o) => !o)}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
              />
            </Nav.Item>
          </Nav>
        </Navbar>
        {mobileMenuOpen && (
          <nav
            aria-label="Mobile navigation"
            style={{
              backgroundColor: t.backgroundPrimary,
              borderBottom: `1px solid ${t.borderPrimary}`,
              position: 'relative',
              zIndex: MOBILE_MENU.zIndex,
            }}
          >
            <MobileNavItem label="Rules" active={active === 'rules'} onClick={() => handleNavigateMobile('/rules')} colors={t} />
            <MobileNavItem label="Card Search" active={active === 'cardSearch'} onClick={() => handleNavigateMobile('/cardsearch')} colors={t} />
            <MobileNavItem label="Set Search" active={active === 'setSearch'} onClick={() => handleNavigateMobile('/setsearch')} colors={t} />

            {isAuthenticated && (
              <>
                <div style={{
                  padding: `${MOBILE_MENU.sectionPaddingTop}px ${MOBILE_MENU.itemPaddingH}px ${MOBILE_MENU.sectionPaddingBottom}px`,
                  fontSize: MOBILE_MENU.sectionFontSize,
                  color: t.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: MOBILE_MENU.sectionLetterSpacing,
                  fontWeight: 600,
                }}>
                  Collection
                </div>
                <MobileNavItem label="Decks" active={active === 'library-decks'} onClick={() => handleNavigateMobile('/library?type=DECK')} colors={t} indent />
                <MobileNavItem label="Binders" active={active === 'library-binders'} onClick={() => handleNavigateMobile('/library?type=TRADE_BINDER')} colors={t} indent />
                <MobileNavItem label="Wish Lists" active={active === 'library-wishlists'} onClick={() => handleNavigateMobile('/library?type=WISHLIST')} colors={t} indent />
                <MobileNavItem label="Collection" active={active === 'collection'} onClick={() => handleNavigateMobile('/collection')} colors={t} indent />
              </>
            )}

            <div style={{
              height: 1,
              backgroundColor: t.borderPrimary,
              margin: `${MOBILE_MENU.dividerMarginV}px 0`,
            }} />

            {isAuthenticated ? (
              <>
                <MobileNavItem label="Settings" active={active === 'settings'} onClick={() => handleNavigateMobile('/settings')} colors={t} />
                <MobileNavItem label="Logout" active={false} onClick={handleLogoutMobile} colors={t} />
              </>
            ) : (
              <>
                <MobileNavItem label="Login" active={false} onClick={() => handleNavigateMobile('/login')} colors={t} />
                <MobileNavItem label="Register" active={false} onClick={() => handleNavigateMobile('/register')} colors={t} />
              </>
            )}
          </nav>
        )}
      </Header>
    );
  }

  return (
    <Header>
      <Navbar appearance={navAppearance}>
        <Navbar.Brand onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          CollectNDeck
        </Navbar.Brand>
        <Nav activeKey={active}>
          <Nav.Item eventKey="rules" onSelect={() => navigate('/rules')}>Rules</Nav.Item>
          <Nav.Item eventKey="cardSearch" onSelect={() => navigate('/cardsearch')}>Card Search</Nav.Item>
          <Nav.Item eventKey="setSearch" onSelect={() => navigate('/setsearch')}>Set Search</Nav.Item>
          {isAuthenticated && (
            <Nav.Menu
              eventKey="collectionMenu"
              title="Collection"
              active={active === 'library' || active === 'library-decks' || active === 'library-binders' || active === 'library-wishlists' || active === 'collection'}
            >
              <Nav.Item eventKey="library-decks" onSelect={() => navigate('/library?type=DECK')}>Decks</Nav.Item>
              <Nav.Item eventKey="library-binders" onSelect={() => navigate('/library?type=TRADE_BINDER')}>Binders</Nav.Item>
              <Nav.Item eventKey="library-wishlists" onSelect={() => navigate('/library?type=WISHLIST')}>Wish Lists</Nav.Item>
              <Nav.Item eventKey="collection" onSelect={() => navigate('/collection')}>Collection</Nav.Item>
            </Nav.Menu>
          )}
        </Nav>
        <Nav pullRight activeKey={active}>
          {isAuthenticated ? (
            <>
              <Nav.Item eventKey="settings" onSelect={() => navigate('/settings')}>Settings</Nav.Item>
              <Nav.Item onSelect={handleLogout}>Logout</Nav.Item>
            </>
          ) : (
            <>
              <Nav.Item onSelect={() => navigate('/login')}>Login</Nav.Item>
              <Nav.Item onSelect={() => navigate('/register')}>Register</Nav.Item>
            </>
          )}
        </Nav>
      </Navbar>
    </Header>
  );
};

export default NavigationBar;
